import { useEffect, useMemo, useState } from 'react';
import { LIMITS, sanitizeCantidad, sanitizeInput } from '../shared/validation.js';
import { TABS, DEFAULT_TAB } from '../shared/tabs.js';
import { CONFIG } from './config.js';
import { Header } from './components/Header.jsx';
import { Footer } from './components/Footer.jsx';
import { CatalogoPanel } from './components/CatalogoPanel.jsx';
import { PreviewPanel } from './components/PreviewPanel.jsx';
import { useToast } from './components/ToastProvider.jsx';
import { loadStoredOrder, saveStoredOrder, clearStoredOrder } from './hooks/usePersistedOrder.js';

export const App = () => {
  const toast = useToast();
  const stored = useMemo(() => loadStoredOrder(), []);
  const [productos, setProductos] = useState([]);
  // Estado de cantidades por tab: { idg: {...}, alternativa: {...} }
  const [cantidadesPorTab, setCantidadesPorTab] = useState(
    () => stored?.cantidadesPorTab || Object.fromEntries(TABS.map(t => [t.id, {}]))
  );
  const [busqueda, setBusqueda] = useState('');
  const [nombre, setNombre] = useState(stored?.nombre || '');
  const [empresa, setEmpresa] = useState(stored?.empresa || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [activeTab, setActiveTab] = useState(stored?.activeTab || DEFAULT_TAB);

  useEffect(() => {
    saveStoredOrder({ cantidadesPorTab, nombre, empresa, activeTab });
  }, [cantidadesPorTab, nombre, empresa, activeTab]);

  const cantidades = cantidadesPorTab[activeTab] || {};
  const setCantidades = (updater) => {
    setCantidadesPorTab(prev => ({
      ...prev,
      [activeTab]: typeof updater === 'function' ? updater(prev[activeTab] || {}) : updater
    }));
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const tab = TABS.find(t => t.id === activeTab);
    if (!tab) {
      setError('Pestaña inválida');
      setLoading(false);
      return;
    }

    fetch(tab.file)
      .then(res => {
        if (!res.ok) throw new Error('Error al cargar productos');
        return res.json();
      })
      .then(data => {
        setProductos(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [activeTab]);

  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      setActiveTab(newTab);
      setBusqueda('');
    }
  };

  const handleCantidadChange = (id, valor) => {
    const limpio = sanitizeCantidad(valor);
    if (limpio === null) return;
    setCantidades(prev => ({ ...prev, [id]: limpio }));
  };

  const handleLimpiar = () => {
    const productosSeleccionados = Object.values(cantidades).filter(qty => {
      const n = parseInt(qty);
      return !isNaN(n) && n > 0;
    }).length;

    if (productosSeleccionados === 0 && !nombre && !empresa) return;

    const mensaje = productosSeleccionados > 0
      ? `¿Estás seguro de que querés limpiar el pedido? Se eliminarán ${productosSeleccionados} ${productosSeleccionados === 1 ? 'producto' : 'productos'} seleccionados.`
      : '¿Estás seguro de que querés limpiar los datos del formulario?';

    if (!confirm(mensaje)) return;

    setCantidadesPorTab(prev => {
      const next = { ...prev, [activeTab]: {} };
      const totalRestante = Object.values(next).reduce(
        (acc, m) => acc + Object.values(m).filter(q => parseInt(q) > 0).length, 0
      );
      if (totalRestante === 0) clearStoredOrder();
      return next;
    });
    setNombre('');
    setEmpresa('');
    setBusqueda('');
  };

  const obtenerTurnstileToken = () => {
    if (!CONFIG.turnstileSiteKey || typeof window.turnstile === 'undefined') return '';
    try { return window.turnstile.getResponse() || ''; }
    catch { return ''; }
  };

  const handleEnviar = async () => {
    if (enviando) return;

    const nombreLimpio = sanitizeInput(nombre, LIMITS.NOMBRE_MAX);
    const empresaLimpia = sanitizeInput(empresa, LIMITS.EMPRESA_MAX);

    if (nombreLimpio.length < 2 || empresaLimpia.length < 2) {
      toast.error('Ingresá un nombre y empresa válidos (mínimo 2 caracteres).');
      return;
    }

    const seleccionados = productos
      .filter(p => {
        const qty = parseInt(cantidades[p.id], 10);
        return !isNaN(qty) && qty > 0 && qty <= LIMITS.CANTIDAD_MAX;
      })
      .map(p => ({
        codigo: String(p.codigo),
        nombre: p.nombre,
        unidad: p.unidad,
        cantidad: parseInt(cantidades[p.id], 10)
      }));

    if (seleccionados.length === 0) return;

    if (CONFIG.turnstileSiteKey && !obtenerTurnstileToken()) {
      toast.error('Completá la verificación anti-bot antes de enviar.');
      return;
    }

    setEnviando(true);
    try {
      const res = await fetch('/.netlify/functions/enviar-pedido', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: nombreLimpio,
          empresa: empresaLimpia,
          productos: seleccionados,
          turnstileToken: obtenerTurnstileToken()
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data.error || 'No se pudo enviar el pedido. Intentá de nuevo.');
        if (window.turnstile && CONFIG.turnstileSiteKey) window.turnstile.reset();
        return;
      }

      window.open(data.url, '_blank', 'noopener,noreferrer');
      toast.success('Pedido listo. Abrimos WhatsApp en una pestaña nueva.');
      if (window.turnstile && CONFIG.turnstileSiteKey) window.turnstile.reset();
    } catch (err) {
      console.error('enviar_pedido_error', err);
      toast.error('Error de red. Revisá tu conexión e intentá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Header />
      <div className="app-container">
        <CatalogoPanel
          productos={productos}
          cantidades={cantidades}
          busqueda={busqueda}
          loading={loading}
          error={error}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onCantidadChange={handleCantidadChange}
          onBusquedaChange={setBusqueda}
          onLimpiar={handleLimpiar}
        />
        <PreviewPanel
          productos={productos}
          cantidades={cantidades}
          nombre={nombre}
          empresa={empresa}
          enviando={enviando}
          onNombreChange={setNombre}
          onEmpresaChange={setEmpresa}
          onEnviar={handleEnviar}
          onCantidadChange={handleCantidadChange}
        />
        <Footer />
      </div>
    </>
  );
};
